import React, { useMemo } from 'react';
import { DBO_V } from '@op/utils/format';
import TableCommonHook from '@op/components/TableCommonHook';
import { Table } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import TextClamp from '@opm/components/TextClamp';
import MinChart from '../../components/MinChart';
import { IDouyinHashtagItem } from './constants';
import type { PageInfoType } from '@op/types/common';
import type { SortBy } from 'tea-component/lib/table/addons';
import ImageList from '@opm/components/ImageList';

const { injectable } = Table.addons;

function getDouyinHashtagId(data: any) {
  if (!data) return '';
  return `${data.date}_${data.rank}_${data.hashtag}`;
}

interface IDouyinHashtagTableProps {
  records: IDouyinHashtagItem[];
  loading: boolean;
  total: number;
  pageInfo: PageInfoType;
  changePageInfo: (pageInfo: PageInfoType) => void;
  /** 受控排序：与 hook 的全量排序共享同一份状态 */
  sortInfo?: [SortBy<any>, (item: SortBy<any>) => void];
  /** 当前选中的行数据（由父组件管理） */
  selectedHashtag?: any;
  /** 行点击回调（由父组件管理） */
  onRowClick?: (record: any) => void;
}

export default function DouyinHashtagTable({
  records,
  loading,
  total,
  pageInfo,
  changePageInfo,
  sortInfo,
  selectedHashtag,
  onRowClick,
}: IDouyinHashtagTableProps) {
  const { isEn, intl } = useIntls();

  const columns = useMemo(() => {
    return [
      {
        key: 'rank',
        header: (
          <div className="dbom-hashtag-rank-header">
            {intl('Rank')}{' '}
            <span className="dbom-hashtag-rank-header__sub">
              ({intl('by {name}', { name: intl('Heat') })})
            </span>
          </div>
        ),
        width: 130,
        align: 'left' as const,
        fixed: 'left' as const,
        render: (row: IDouyinHashtagItem) => {
          return (
            <div className="dbom-hashtag-rank dbom-hashtag-rank--douyin">
              <p className={`dbom-rank dbom-rank0${row.rank}`}>{row.rank}</p>
            </div>
          );
        },
      },
      {
        key: 'hashtag',
        header: intl('Hashtags'),
        width: 360,
        fixed: 'left' as const,
        render: (row: IDouyinHashtagItem) => {
          const mainText = isEn ? row.hashtag_en || row.hashtag : row.hashtag;
          if (!mainText) return '-';
          return (
            <div className="dbom-douyin-hashtag-title-cell">
              <TextClamp rowNum={1} text={mainText} placement="top" />
            </div>
          );
        },
      },
      {
        key: 'heat',
        header: intl('Heat'),
        width: 100,
        align: 'right' as const,
        render: (row: IDouyinHashtagItem) => {
          return (
            <span className="dbom-douyin-heat-cell">
              {DBO_V.reviews(row.heat)}
            </span>
          );
        },
      },
      {
        key: 'heat_trend',
        header: intl('Heat Trend'),
        width: 200,
        render: (row: IDouyinHashtagItem) => {
          const timeline = row.heat_trend || [];
          if (!timeline.length) return '-';
          return (
            <div className="dbom-douyin-trend-cell">
              <MinChart timeline={timeline} />
            </div>
          );
        },
      },
      {
        key: 'video',
        width: 150,
        header: intl({ id: 'Video' }),
        render: (cvm: any) => {
          const { top_videos: video } = cvm;
          if (!video) {
            return null;
          }
          return (
            <ImageList
              list={video}
              keyField="video_url"
              imageField="video_cover"
              onClick={() => onRowClick?.(cvm)} 
            />
          );
        },
      }
    ];
  }, [isEn, intl]);

  return (
    <div className="dbom-douyin-hashtag-table">
      <TableCommonHook
        records={records}
        recordKey={(row: IDouyinHashtagItem) =>
          `${row.date}_${row.rank}_${row.hashtag}`
        }
        columns={columns}
        loading={loading}
        total={total}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        minWidth={880}
        tableSticky
        sortColumns={['heat']}
        sortInfo={sortInfo}
        pageSizeOptions={[10, 20, 30]}
        addons={[
          injectable({
            row: (_props: any, context: any) => {
              const isSelected =
                getDouyinHashtagId(context.record) ===
                getDouyinHashtagId(selectedHashtag);
              return {
                className: isSelected
                  ? 'dbom-selected-table-row'
                  : '',
                onClick: () => onRowClick?.(context.record),
                style: { cursor: 'pointer' },
              };
            },
          }),
        ]}
      />
    </div>
  );
}
